import React, { useMemo, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { CircuulReportSortBy } from '../../../types/global/circuulCampaignReport.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../brandDashboardTokens';
import { useAppInstallCampaignReport } from './useAppInstallCampaignReport';

function formatUsd(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: n >= 10 ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(n);
}

function StatCard({
    label,
    value,
    icon,
    accent,
    hint,
}: {
    label: string;
    value: string | number;
    icon: React.ReactNode;
    accent?: string;
    hint?: string;
}) {
    return (
        <Box
            sx={{
                width: '100%',
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 2,
                p: 2.5,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, color: 'rgba(255,255,255,0.5)' }}>
                {icon}
                <Typography
                    sx={{
                        fontSize: 10,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                    }}>
                    {label}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: { xs: 26, md: 32 },
                    color: accent || BRAND_LUXE_PRIMARY,
                }}>
                {value}
            </Typography>
            {hint ? (
                <Typography sx={{ mt: 0.75, fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.4 }}>
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );
}

export function AppInstallCampaignReport({ campaignId }: { campaignId: string }) {
    const report = useAppInstallCampaignReport(campaignId);
    const [valuePerCustomer, setValuePerCustomer] = useState('');
    const conversionLabel = report.data?.summary.conversion_label || 'installs';
    const kindLabel = report.data?.campaign.kind === 'web_visit' ? 'Web visit' : 'App install';
    const singularConversion = conversionLabel.replace(/s$/, '');
    const summary = report.data?.summary;
    const installs = summary?.total_installs ?? 0;
    const spendUsd = summary?.total_spend_usd ?? 0;
    const cpiLow = summary?.industry_cpi_low_usd ?? 4;
    const cpiHigh = summary?.industry_cpi_high_usd ?? 6;

    const roiCalc = useMemo(() => {
        const vpc = Number.parseFloat(valuePerCustomer);
        if (!Number.isFinite(vpc) || vpc <= 0 || installs <= 0) return null;
        const estimatedRevenue = vpc * installs;
        const profit = estimatedRevenue - spendUsd;
        const roiPercent = spendUsd > 0 ? (profit / spendUsd) * 100 : null;
        return {
            estimatedRevenue,
            profit,
            roiPercent,
        };
    }, [valuePerCustomer, installs, spendUsd]);

    if (report.loading && !report.data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
            </Box>
        );
    }

    if (report.error && !report.data) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{ color: '#ff8a80', mb: 2 }}>{report.error}</Typography>
                <Button onClick={() => void report.reload()} sx={{ color: BRAND_LUXE_PRIMARY, textTransform: 'none' }}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1.5,
                }}>
                <Typography
                    sx={{
                        fontSize: 12,
                        color: 'rgba(255,255,255,0.45)',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                    }}>
                    {kindLabel} performance · CPA attribution
                </Typography>
                <Button
                    size="small"
                    startIcon={
                        report.loading ? (
                            <CircularProgress size={14} sx={{ color: BRAND_LUXE_PRIMARY }} />
                        ) : (
                            <RefreshIcon />
                        )
                    }
                    onClick={() => void report.reload()}
                    disabled={report.loading}
                    sx={{ color: BRAND_LUXE_PRIMARY, textTransform: 'none', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    Refresh
                </Button>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: 1.5,
                }}>
                <StatCard
                    label="Total clicks"
                    value={summary?.total_clicks ?? 0}
                    icon={<TouchAppOutlinedIcon sx={{ fontSize: 14 }} />}
                />
                <StatCard
                    label={`Total ${conversionLabel}`}
                    value={summary?.total_installs ?? 0}
                    icon={<GetAppOutlinedIcon sx={{ fontSize: 14 }} />}
                />
                <StatCard
                    label="Total spend"
                    value={formatUsd(summary?.total_spend_usd)}
                    icon={<AttachMoneyOutlinedIcon sx={{ fontSize: 14 }} />}
                />
                <StatCard
                    label="Industry savings"
                    value={formatUsd(summary?.industry_savings_usd)}
                    icon={<SavingsOutlinedIcon sx={{ fontSize: 14 }} />}
                    accent={
                        summary?.industry_savings_usd == null
                            ? undefined
                            : summary.industry_savings_usd >= 0
                            ? BRAND_LUXE_SECONDARY
                            : '#ff8a80'
                    }
                    hint={
                        installs > 0
                            ? `vs $${cpiLow}–$${cpiHigh} industry CPI · ${
                                  summary?.industry_savings_percent != null
                                      ? `${summary.industry_savings_percent > 0 ? '+' : ''}${
                                            summary.industry_savings_percent
                                        }%`
                                      : '—'
                              } · range ${formatUsd(summary?.industry_savings_low_usd)}–${formatUsd(
                                  summary?.industry_savings_high_usd,
                              )}`
                            : `Most platforms charge $${cpiLow}–$${cpiHigh} per ${singularConversion}`
                    }
                />
                <StatCard
                    label={`Cost / ${singularConversion}`}
                    value={formatUsd(summary?.cost_per_install_usd)}
                    icon={<GetAppOutlinedIcon sx={{ fontSize: 14 }} />}
                />
                <StatCard
                    label="Cost / click"
                    value={formatUsd(summary?.cost_per_click_usd)}
                    icon={<TouchAppOutlinedIcon sx={{ fontSize: 14 }} />}
                />
                <StatCard
                    label="Conversion rate"
                    value={`${summary?.conversion_rate ?? 0}%`}
                    icon={<PublicOutlinedIcon sx={{ fontSize: 14 }} />}
                />
                <StatCard
                    label="Creators tracked"
                    value={summary?.creators_count ?? 0}
                    icon={<PeopleOutlineIcon sx={{ fontSize: 14 }} />}
                />
            </Box>
            {(summary?.total_budget_usd != null && summary.total_budget_usd > 0) ||
            (summary?.available_budget_usd != null && summary.available_budget_usd > 0) ? (
                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                    Budget {formatUsd(summary?.total_budget_usd)} · Remaining {formatUsd(summary?.available_budget_usd)}{' '}
                    · CPA {formatUsd(summary?.cpa_usd)}
                </Typography>
            ) : null}

            <Box
                sx={{
                    bgcolor: BRAND_SURFACE_CONTAINER,
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 2,
                    p: { xs: 2, md: 2.5 },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, color: 'rgba(255,255,255,0.55)' }}>
                    <CalculateOutlinedIcon sx={{ fontSize: 16 }} />
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                        }}>
                        ROI calculator
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', mb: 2, maxWidth: 520 }}>
                    Enter what a customer is worth to you. We estimate revenue from your {installs.toLocaleString()}{' '}
                    attributed {conversionLabel} and compare it to spend.
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'minmax(180px, 240px) 1fr' },
                        gap: 2,
                        alignItems: 'start',
                    }}>
                    <TextField
                        size="small"
                        label={`Value per ${singularConversion}`}
                        type="number"
                        value={valuePerCustomer}
                        onChange={e => setValuePerCustomer(e.target.value)}
                        placeholder="e.g. 25"
                        inputProps={{ min: 0, step: '0.01' }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#fff',
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                '&:hover fieldset': { borderColor: BRAND_OUTLINE_VARIANT },
                                '&.Mui-focused fieldset': { borderColor: BRAND_LUXE_PRIMARY },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.45)' },
                            '& .MuiInputAdornment-root': { color: 'rgba(255,255,255,0.5)' },
                        }}
                    />
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                            gap: 1.5,
                        }}>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: 'rgba(255,255,255,0.4)',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    mb: 0.5,
                                }}>
                                Est. revenue
                            </Typography>
                            <Typography
                                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: BRAND_LUXE_PRIMARY }}>
                                {roiCalc ? formatUsd(roiCalc.estimatedRevenue) : '—'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: 'rgba(255,255,255,0.4)',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    mb: 0.5,
                                }}>
                                Profit
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 20,
                                    color:
                                        roiCalc == null
                                            ? BRAND_LUXE_PRIMARY
                                            : roiCalc.profit >= 0
                                            ? BRAND_LUXE_SECONDARY
                                            : '#ff8a80',
                                }}>
                                {roiCalc ? formatUsd(roiCalc.profit) : '—'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: 'rgba(255,255,255,0.4)',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    mb: 0.5,
                                }}>
                                ROI
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 20,
                                    color:
                                        roiCalc?.roiPercent == null
                                            ? BRAND_LUXE_PRIMARY
                                            : roiCalc.roiPercent >= 0
                                            ? BRAND_LUXE_SECONDARY
                                            : '#ff8a80',
                                }}>
                                {roiCalc?.roiPercent == null
                                    ? '—'
                                    : `${roiCalc.roiPercent > 0 ? '+' : ''}${roiCalc.roiPercent.toFixed(1)}%`}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Creators */}
            <Box
                sx={{
                    bgcolor: BRAND_SURFACE_CONTAINER,
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 2,
                    p: { xs: 2, md: 3 },
                }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: '#fff', flex: 1 }}>
                        Creators
                    </Typography>
                    <TextField
                        size="small"
                        placeholder="Search name or code…"
                        value={report.search}
                        onChange={e => report.setSearch(e.target.value)}
                        sx={{
                            minWidth: 180,
                            '& .MuiOutlinedInput-root': {
                                color: '#fff',
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                '& fieldset': { borderColor: `${BRAND_OUTLINE_VARIANT}66` },
                            },
                        }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Sort by"
                        value={report.sortBy}
                        onChange={e => report.setSortBy(e.target.value as CircuulReportSortBy)}
                        sx={{
                            minWidth: 150,
                            '& .MuiOutlinedInput-root': {
                                color: '#fff',
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                '& fieldset': { borderColor: `${BRAND_OUTLINE_VARIANT}66` },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                        }}>
                        <MenuItem value="downloads">Most {conversionLabel}</MenuItem>
                        <MenuItem value="clicks">Most clicks</MenuItem>
                        <MenuItem value="conversion_rate">Conversion rate</MenuItem>
                        <MenuItem value="name">Name</MenuItem>
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label="Order"
                        value={report.sortOrder}
                        onChange={e => report.setSortOrder(e.target.value as 'asc' | 'desc')}
                        sx={{
                            minWidth: 110,
                            '& .MuiOutlinedInput-root': {
                                color: '#fff',
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                '& fieldset': { borderColor: `${BRAND_OUTLINE_VARIANT}66` },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                        }}>
                        <MenuItem value="desc">High → low</MenuItem>
                        <MenuItem value="asc">Low → high</MenuItem>
                    </TextField>
                </Box>

                {!report.creators.length ? (
                    <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, py: 3, textAlign: 'center' }}>
                        No creator click or {conversionLabel} data yet.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box
                            sx={{
                                display: { xs: 'none', sm: 'grid' },
                                gridTemplateColumns: 'minmax(0,2fr) 80px 90px 90px 80px',
                                gap: 1,
                                px: 1.5,
                                pb: 1,
                            }}>
                            {['Creator', 'Code', 'Clicks', conversionLabel, 'Rate'].map(h => (
                                <Typography
                                    key={h}
                                    sx={{
                                        fontSize: 10,
                                        color: 'rgba(255,255,255,0.4)',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                    }}>
                                    {h}
                                </Typography>
                            ))}
                        </Box>
                        {report.creators.map(row => (
                            <Box
                                key={row.code}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr 1fr',
                                        sm: 'minmax(0,2fr) 80px 90px 90px 80px',
                                    },
                                    gap: 1,
                                    alignItems: 'center',
                                    px: 1.5,
                                    py: 1.25,
                                    borderRadius: 1.5,
                                    bgcolor: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(255,255,255,0.04)',
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                        minWidth: 0,
                                        gridColumn: { xs: '1 / -1', sm: 'auto' },
                                    }}>
                                    <Box
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            bgcolor: '#201f1f',
                                            flexShrink: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        {row.profile_picture ? (
                                            <Box
                                                component="img"
                                                src={row.profile_picture}
                                                alt=""
                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)' }}>
                                                {row.name[0]?.toUpperCase() || '?'}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 14,
                                            color: '#fff',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {row.name}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{ fontSize: 12, color: BRAND_LUXE_PRIMARY, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                    {row.code}
                                </Typography>
                                <Typography sx={{ fontSize: 14, color: '#fff' }}>{row.clicks}</Typography>
                                <Typography
                                    sx={{ fontSize: 14, color: BRAND_LUXE_SECONDARY, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                    {row.downloads}
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
                                    {row.conversion_rate}%
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Geo */}
            <Box
                sx={{
                    bgcolor: BRAND_SURFACE_CONTAINER,
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 2,
                    p: { xs: 2, md: 3 },
                }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: '#fff', flex: 1 }}>
                        Geographic distribution
                    </Typography>
                    <TextField
                        select
                        size="small"
                        label="Sort geo by"
                        value={report.geoSortBy}
                        onChange={e => report.setGeoSortBy(e.target.value as 'downloads' | 'clicks')}
                        sx={{
                            minWidth: 150,
                            '& .MuiOutlinedInput-root': {
                                color: '#fff',
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                '& fieldset': { borderColor: `${BRAND_OUTLINE_VARIANT}66` },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                        }}>
                        <MenuItem value="downloads">Most {conversionLabel}</MenuItem>
                        <MenuItem value="clicks">Most clicks</MenuItem>
                    </TextField>
                </Box>

                {!report.data?.geo?.length ? (
                    <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, py: 3, textAlign: 'center' }}>
                        No location data yet. New clicks and {conversionLabel} will capture country from the request.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {report.data!.geo.map(row => {
                            const widthPct = Math.max(
                                4,
                                Math.round(((row.downloads || 0) / report.maxGeoDownloads) * 100),
                            );
                            return (
                                <Box key={`${row.country_code || 'xx'}-${row.country}`}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                                        <Typography
                                            sx={{ fontSize: 13, color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                            {row.country}
                                            {row.country_code ? (
                                                <Box
                                                    component="span"
                                                    sx={{ color: 'rgba(255,255,255,0.35)', ml: 1, fontWeight: 400 }}>
                                                    {row.country_code}
                                                </Box>
                                            ) : null}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                                            {row.downloads} {conversionLabel} · {row.clicks} clicks
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            height: 8,
                                            borderRadius: 999,
                                            bgcolor: 'rgba(255,255,255,0.06)',
                                            overflow: 'hidden',
                                        }}>
                                        <Box
                                            sx={{
                                                width: `${widthPct}%`,
                                                height: '100%',
                                                borderRadius: 999,
                                                background: `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                                            }}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
